import {
  getParticipantLineItemGroups,
  type OrderingContext,
  prepareLedger,
} from '@order/features-ordering';
import {
  getSharedLineItemAttribution,
  getSharedLineItemCartLineItemTelemetry,
} from '@order/shared-line-item';
import { type TelemetryEvent, telemetryProductFromInput } from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

/**
 * Converts the machine context in the telemetry event data for purchase events.
 */
export const getCheckoutTelemetryData = (
  context: OrderingContext,
  selectedPaymentMethodType?: string,
): TelemetryEvent<'checkout.start'>['payload'] => {
  const sharedLineItemAttribution = getSharedLineItemAttribution();

  const products = context.cart.lineItems?.map(
    ({ id: lineItemId, product, cost, quantity, customName }) => {
      const sharedLineItemTelemetry = getSharedLineItemCartLineItemTelemetry({
        attribution: sharedLineItemAttribution,
        cartLineItemId: lineItemId,
      });

      return telemetryProductFromInput({
        ...product,
        ...sharedLineItemTelemetry,
        restaurantId: context.cart?.restaurantId,
        location: context.cart?.locationName,
        cost,
        quantity,
        name: customName ?? product?.name,
      });
    },
  );
  const hasSharedLineItem = products?.some(({ isSharedLineItem }) => Boolean(isSharedLineItem));

  const bagCount = products
    ?.map(({ quantity }) => quantity)
    .reduce((a, b) => (a ?? 0) + (b ?? 0), 0);

  const isGroupOrder = context.cart.isGroupOrder;
  const groupOrderParticipantCount = isGroupOrder
    ? getParticipantLineItemGroups(context.cart.lineItems ?? []).length
    : undefined;

  const { total } = prepareLedger({
    ledger: context.cart?.ledger,
    tip: context.selectedTip,
    availableCredit: context.availableCredit,
    shouldUseCredit: context.shouldUseCredit,
    amountEligibleForFlex: context.amountEligibleForFlex,
    shouldUseFlex: context.shouldUseFlex,
  });

  return {
    selectedPaymentMethodType,
    orderId: context.cart?.orderId,
    restaurantId: context.cart?.restaurantId,
    restaurantName: context.cart?.locationName,
    orderType: context.cart?.orderChannel,
    products,
    bagCount,
    orderValue: convertPriceToFloat(total),
    street: context.cart?.deliveryOrderDetail?.address?.street,
    city: context.cart?.deliveryOrderDetail?.address?.city,
    state: context.cart?.deliveryOrderDetail?.address?.state,
    zipCode: context.cart?.deliveryOrderDetail?.address?.zipCode,
    country: context.cart?.deliveryOrderDetail?.address?.country,
    isNewCustomer: context.customer.isNewCustomer,
    customer: {
      firstName: context.customer.firstName,
      lastName: context.customer.lastName,
      email: context.customer.email,
      phoneNumber: context.customer.phoneNumber,
    },
    hasSharedLineItem: Boolean(hasSharedLineItem),
    isGroupOrder,
    groupOrderParticipantCount,
  };
};
